import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, setToken, getToken, formatApiError } from "../lib/api";
import { beginTelemetrySession, endTelemetrySession, trackAppOpen, trackSignup } from "../lib/telemetry";
import {
  installPreparednessRetryListener,
  retryPendingPreparednessReward,
} from "../lib/preparednessReward";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // status: "loading" | "authed" | "guest"
  const [status, setStatus] = useState(getToken() ? "loading" : "guest");
  const [user, setUser] = useState(null);

  const applyAuth = useCallback((data) => {
    setToken(data.token);
    setUser(data.user);
    setStatus("authed");
    beginTelemetrySession();
    trackAppOpen(data.user?.id);
  }, []);

  useEffect(() => {
    let alive = true;
    const t = getToken();
    if (!t) {
      setStatus("guest");
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        if (!alive) return;
        setUser(res.data);
        setStatus("authed");
        beginTelemetrySession();
        trackAppOpen(res.data?.id);
      })
      .catch(() => {
        if (!alive) return;
        setToken(null);
        setUser(null);
        setStatus("guest");
      });
    return () => {
      alive = false;
    };
  }, []);

  // Listen for the WS `tokens.update` event and reflect it in the user state.
  useEffect(() => {
    const handler = (e) => {
      const balance = e?.detail?.balance;
      if (typeof balance !== "number") return;
      setUser((u) => (u ? { ...u, nexatokens_balance: balance } : u));
    };
    window.addEventListener("queonda.tokens.update", handler);
    return () => window.removeEventListener("queonda.tokens.update", handler);
  }, []);

  // Fase 8 — Preparedness reward retry. Every time the user transitions to
  // authed, replay a pending claim (if any). Also installs a global `online`
  // listener for retries while the session is live. We stash the current
  // user in a ref so the listener always sees the latest reference without
  // re-attaching on each render.
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    if (!user) return undefined;
    retryPendingPreparednessReward({
      user,
      onBalanceUpdate: (balance) =>
        setUser((u) => (u ? { ...u, nexatokens_balance: balance } : u)),
    }).catch(() => {});
    const cleanup = installPreparednessRetryListener(
      () => userRef.current,
      (balance) =>
        setUser((u) => (u ? { ...u, nexatokens_balance: balance } : u))
    );
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const login = async ({ email, password }) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyAuth(data);
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    applyAuth(data);
    // Signup telemetry event — fired ONLY for fresh account creation.
    try {
      trackSignup(data.user?.id);
    } catch (_e) {
      /* ignore */
    }
    return data.user;
  };

  const loginWithGoogle = async ({ session_id, referred_by, terms_accepted }) => {
    const { data } = await api.post("/auth/google", {
      session_id,
      referred_by: referred_by || null,
      terms_accepted: !!terms_accepted,
    });
    applyAuth(data);
    // If the backend indicated this was a first-time creation (not existing
    // login), fire the signup event too. Backend returns `is_new` to signal.
    if (data.is_new) {
      try {
        trackSignup(data.user?.id);
      } catch (_e) {
        /* ignore */
      }
    }
    return data.user;
  };

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (_e) {
      return null;
    }
  }, []);

  const logout = () => {
    setToken(null);
    setUser(null);
    setStatus("guest");
    endTelemetrySession();
  };

  const updateProfile = async (patch) => {
    const { data } = await api.put("/profile", patch);
    setUser(data);
    return data;
  };

  const value = useMemo(
    () => ({
      status,
      user,
      login,
      signup,
      loginWithGoogle,
      logout,
      updateProfile,
      refreshUser,
      formatApiError,
    }),
    [status, user, loginWithGoogle, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}
